import * as React from 'react';
import { useState, useEffect } from 'react';
import { TabContent, TabPane, Nav, NavItem, NavLink, Card, Button, CardTitle, CardText, Row, Col, Input, Label } from 'reactstrap';
import Page from '../Page';
import { useAppSelector, useAppDispatch } from '../../hooks/index';
import { Entity, getSelectedEntity } from '../../redux/EntitySlice';
import { Afr, getSelectedAfr, updateVersionStateValue, generateLocalStateFromEventProperties, versionStateValue, updateTableFact, existFactsInTable, getTable, FactTable } from '../../redux/AfrSlice';
import ExpendituresCoreDetail from '../AfrCore/ExpendituresCoreDetail';
import { setIsDirty, isAdmin } from '../../redux/MiscSlice';
import { resetConceptState } from "../../redux/ConceptSlice";
import utilityHelper from '../../utils/utilityHelper';
import { useLocation } from 'react-router-dom';
import enums from '../../utils/enums';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';

const ExpendituresCore = (props: { userRole: string }) => {

    const dispatch = useAppDispatch();
    const parameters = useLocation();

    /* Tabs logic */
    const [activeTab, setActiveTab] = useState("1");
    const [hasFacts, setHasFacts] = useState(true);

    const toggle = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    const activeClass = (tab: string) => {
        return (tab === activeTab) ? "active" : "";
    }


    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);
    const afrYear = currentAfr ? currentAfr.year : 0;
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    const isAdminUser: boolean = useAppSelector(isAdmin);
    const [state, setState] = useState(generateLocalStateFromEventProperties(currentAfr, ["workState_expendituresComplete", "workState_noExpenditures", "reviewState_expendituresReviewed"]));
    const [titleDocumentation, setTitleDocumentation] = useState<string[]>([]);

    const currVersion = function () {
        if (currentAfr) {
            if (currentAfr.currentversion) {
                if (currentAfr.currentversion.versionState) {
                    return currentAfr.currentversion.versionState;
                }
            }
        }
        return null;
    }

    const updateHasFacts = function (bHasFacts: boolean) {
        if (bHasFacts) {
            setHasFacts(true);
            return;
        }
        if (currentAfr) {
            var hFlag = existFactsInTable(currentAfr, ["uas_034_expenditures"]);
            setHasFacts(hFlag);
        } else {
            setHasFacts(false);
        }
    }
    const handleChangeCheckbox = function (event: any) {

        var checkValues = JSON.parse(JSON.stringify(state));
        var key: string = event.target.name;
        var value: boolean = event.target.checked
        checkValues[key] = value;

        setState(checkValues);

        // here call to dispatcher sending:
        var eventFirstlevel = key.split("_")[0]
        var eventSecondlevel = key.split("_")[1]

        // Update global flag for this event in redux
        dispatch(updateVersionStateValue({ propGroup: eventFirstlevel, propName: eventSecondlevel, boolValue: value, value: null }));

        // set the dirty flag to TRUE
        dispatch(setIsDirty({ IsDirty: true, Page: "" }));
    }

    useEffect(() => {
        setTitleDocumentation([]);
        if (!currentAfr) return;
        if (!currentAfr.currentversion) return;

        // get state from redux
        var newLocalState = generateLocalStateFromEventProperties(currentAfr, ["workState_expendituresComplete", "workState_noExpenditures", "reviewState_expendituresReviewed"])
        // enable/disable checkbox when there are (no) facts 
        setHasFacts(existFactsInTable(currentAfr, ["uas_034_expenditures"]))
        var tbl = getTable(currentAfr, "uas_034_expenditures");
        if (tbl && tbl.structure) {
            setTitleDocumentation(tbl.structure.documentationByYear.split(/<br\/>/g));
        }

        // create a local state from redux
        setState(newLocalState)

    }, [currentAfr, currVersion()])

    useEffect(() => {

        dispatch(resetConceptState({}));

    }, [activeTab]);

    useEffect(() => {
        if (parameters.state && parameters.state.tabId && parameters.state.tabId.length > 0) {
            toggle(parameters.state.tabId);
        }

    }, [parameters.state]);

    return (
        <React.Fragment>
            <Page>
                {entity != null ? (<h2>Expenditures for {utilityHelper.getEntityName(entity, currentAfr)} ({utilityHelper.getEntityCode(entity, currentAfr)}), {afrYear}</h2>) : (<></>)}
                
                <Row>
                    <Col>
                        <div className="box-events pl-2 pr-3 py-3 my-2">
                            <div>
                                <Label className="ml-4" check>
                                    <Input type="checkbox"
                                        name="workState_noExpenditures"
                                        onChange={handleChangeCheckbox}
                                        disabled={hasFacts || utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                                            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)}
                                        checked={state["workState_noExpenditures"] ? true : false}
                                    />{' '} <b>No Expenditures</b>
                                </Label>
                                <Label className="ml-5" check>
                                    <Input type="checkbox"
                                        name="workState_expendituresComplete"
                                        onChange={handleChangeCheckbox}
                                        disabled={!(hasFacts || (state["workState_noExpenditures"] ? true : false)) ||
                                            (state["workState_expendituresComplete"] ? utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified) : false)
                                            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin) }
                                        checked={state["workState_expendituresComplete"] ? true : false}
                                    />{' '} <b>Done</b>
                                </Label>
                                {isAdminUser && (
                                    <Label className="ml-5" check>
                                        <Input type="checkbox"
                                            name="reviewState_expendituresReviewed"
                                            onChange={handleChangeCheckbox}
                                            checked={state["reviewState_expendituresReviewed"] ? true : false}
                                            disabled={!utilityHelper.checkStatusList(currentAfr, enums.certifiedRecertified)}
                                        /><b>Reviewed</b>
                                    </Label>)}
                            </div>
                        </div>
                        <div>
                            <Nav tabs>
                                <NavItem>
                                    <NavLink
                                        className={activeClass('1')}
                                        onClick={() => { toggle('1'); }}
                                    >
                                        Instructions
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        className={activeClass('2')}
                                        onClick={() => { toggle('2'); }}
                                    >
                                        Expenditures
                                    </NavLink>
                                </NavItem>
                            </Nav>
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <Row>
                                        <Col sm="12">
                                            {titleDocumentation.length === 0
                                                ?
                                                <h5>
                                                    <p>Use the Expenditure tab to enter expenditure balances by account code and fund group. Use the buttons in the Account Grid Editor to add and delete lines of expenditure data. Fields will only allow whole numbers (no decimals).</p>
                                                    <p>Before the system will allow you to submit your AFR, you must check the “Done” checkbox. This is still true if you have checked the “No Expenditures” checkbox. If the “Done” checkbox is not checked at the time you submit the AFR, you will receive an error message and the AFR will not submit.</p>
                                                </h5>
                                                :
                                                <h5>
                                                    {titleDocumentation.map((option: string, idx) => (
                                                        <div key={"expdoc" + idx} dangerouslySetInnerHTML={{ __html: "<p>" + option + "</p>" }} ></div>
                                                    ))}
                                                </h5>
                                            }
                                        </Col>
                                    </Row>
                                </TabPane>
                                <TabPane tabId="2">
                                    <Row>
                                        <Col sm="12">
                                            {(activeTab === "2") && (<ExpendituresCoreDetail doneFlag={"workState_expendituresComplete"} noFlag={"workState_noExpenditures"} setHasData={updateHasFacts} fundGroup={null} />)}
                                        </Col>
                                    </Row>
                                </TabPane>
                            </TabContent>                            
                        </div>
                    </Col>
                </Row>
            </Page>
        </React.Fragment>
    );
}

export default ExpendituresCore;